import * as React from "react"
import { type ColGroupProps } from "@digits-shared/components/UI/VirtualizedTable/vTableTypes"
import { type ColumnDef, type GroupColumnDef } from "@tanstack/react-table"

/*
  COMPONENTS
*/
export const ColGroup = <TData extends object>({ columns }: ColGroupProps<TData>) => {
  const leafColumns = getLeafColumns(columns)
  const flexColumns = leafColumns.filter((c) => c.size === undefined && c.minSize === undefined)
  const flexColumnsPercent = flexColumns.length ? 99 : 0

  return (
    <colgroup>
      {leafColumns.map((column, index) => {
        let width = flexColumnsPercent ? `${flexColumnsPercent}%` : "auto"
        if (column.size !== undefined) {
          width = `${column.size}px`
        }
        if (column.minSize !== 0 && column.minSize !== undefined) {
          width = `${column.minSize}px`
        }

        const { header } = column
        const key =
          column.id ??
          ("accessorKey" in column
            ? column.accessorKey.toString()
            : typeof header === "string"
              ? header
              : index)

        return (
          <col
            key={key}
            data-column-name={key}
            width={width}
            style={{ minWidth: column.minSize ? `${column.minSize}px` : undefined }}
          />
        )
      })}
    </colgroup>
  )
}

function getLeafColumns<TData extends object>(cols: ColumnDef<TData>[]): ColumnDef<TData>[] {
  return cols.flatMap((col) => {
    if (isGroup(col) && col.columns) {
      return getLeafColumns(col.columns)
    }
    return col
  })
}

function isGroup<TData extends object>(column: ColumnDef<TData>): column is GroupColumnDef<TData> {
  return "columns" in column
}
